import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ThresholdConfig } from '@/components/dashboard/TrendSparkline';
import { TrendPeriod } from '@/components/dashboard/TrendPeriodToggle';

interface CostSavingsGoal {
  id: string;
  name: string;
  target_amount: number;
  period_type: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface GoalThresholds {
  sessions?: ThresholdConfig;
  patients?: ThresholdConfig;
  redFlags?: ThresholdConfig;
}

export function useGoalThresholds(agencyId?: string, trendPeriod: TrendPeriod = 'week') {
  const [thresholds, setThresholds] = useState<GoalThresholds>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        
        // Fetch active goals within current date range
        let query = supabase
          .from('cost_savings_goals')
          .select('*')
          .eq('is_active', true)
          .lte('start_date', today)
          .gte('end_date', today);
        
        if (agencyId) {
          query = query.eq('agency_id', agencyId);
        }

        const { data: goals, error } = await query;

        if (error) {
          console.error('Error fetching goals:', error);
          setLoading(false);
          return;
        }

        if (!goals || goals.length === 0) {
          setThresholds({});
          setLoading(false);
          return;
        }

        // Calculate thresholds based on active goals
        const activeGoal = goals[0] as CostSavingsGoal;
        
        // Calculate the number of periods in the goal
        const startDate = new Date(activeGoal.start_date);
        const endDate = new Date(activeGoal.end_date);
        const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Calculate periods based on trend period
        let periodsInGoal: number;
        let segmentsInPeriod: number;
        
        switch (trendPeriod) {
          case 'week':
            periodsInGoal = Math.ceil(totalDays / 7);
            segmentsInPeriod = 7;
            break;
          case 'month':
            periodsInGoal = Math.ceil(totalDays / 30);
            segmentsInPeriod = 8;
            break;
          case 'year':
            periodsInGoal = Math.ceil(totalDays / 365);
            segmentsInPeriod = 12;
            break;
          default:
            periodsInGoal = Math.ceil(totalDays / 7);
            segmentsInPeriod = 7;
        }

        // Target per segment (for sparkline visualization)
        // Assuming each prevented ER visit saves ~$2,350 average
        const avgSavingsPerCase = 2350;
        const targetCasesPerPeriod = Math.ceil(activeGoal.target_amount / (periodsInGoal * avgSavingsPerCase));
        const targetPerSegment = Math.max(1, Math.ceil(targetCasesPerPeriod / segmentsInPeriod));

        // Set thresholds for different metrics
        setThresholds({
          sessions: {
            value: targetPerSegment,
            type: 'target',
            label: `Goal: ${activeGoal.name}`
          },
          patients: {
            value: Math.ceil(targetPerSegment * 0.8), // ~80% of sessions target for new patients
            type: 'target',
            label: `Goal: ${activeGoal.name}`
          },
          redFlags: {
            value: Math.max(1, Math.ceil(targetPerSegment * 0.1)), // Red flags should be low
            type: 'warning',
            label: 'Warning threshold'
          }
        });
      } catch (error) {
        console.error('Error calculating goal thresholds:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [agencyId, trendPeriod]);

  return { thresholds, loading };
}
